import { ReactElement, ReactNode, useState } from 'react';
import cn from 'classnames';
import Link from 'next/link';
import { pickBy } from 'lodash-es';
import { SelectionT } from '@/lib/collections/catalogue/helpers';

type InputAttributes = React.AnchorHTMLAttributes<HTMLAnchorElement>;

const ANCHOR_BASE =
  'w-full h-full flex items-center justify-center cursor-pointer block';
const ANCHOR_INACTIVE = 'border-gray-700 lg:border-gray-500 rounded-full';
const ANCHOR_DISABLED = 'hover:cursor-not-allowed';

export const Tooltip = () => (
  <>
    <span className="absolute bottom-[calc(100%+5px)] left-1/2 mb-2 hidden -translate-x-1/2 whitespace-nowrap rounded bg-gray-100 p-2 text-xs text-black group-hover:inline-block xs:block">
      Other option(s) available
    </span>
    <span className="border-t-blue-300group-hover:inline-block absolute bottom-full left-1/2 mb-2 hidden -translate-x-1/2 border-[6px] border-b-0 border-l-transparent border-r-transparent xs:block"></span>
  </>
);

const Color = (
  props: InputAttributes & {
    query: null | SelectionT;
    currentColor: string;
    active: boolean;
    onHover: (color: string) => void;
    onHoverOut: () => void;
    disabled?: boolean;
    suggested?: boolean | undefined;
    setSuggestedVariant: React.Dispatch<React.SetStateAction<string>>;
    variant: string;
  },
) => {
  const [isOpen, setIsOpen] = useState(false);
  const updatedQuery = pickBy(props.query, (value, key) => key !== 'suggested');
  const {
    children,
    query,
    currentColor,
    active,
    onHover,
    onHoverOut,
    disabled = false,
    suggested,
    setSuggestedVariant,
    variant,
    ...linkProps
  } = props;

  return (
    <div
      className={cn(
        { 'cursor-not-allowed': query == null || disabled },
        'h-[50px] w-[50px] xs:h-[26px] xs:w-[26px]',
      )}
      onClick={() => {
        if (suggested) setIsOpen(false);
      }}
    >
      <Link
        href={{
          pathname: '/collections/[handle]',
          query: updatedQuery,
        }}
        scroll={false}
        shallow
      >
        <a
          aria-label={`option(s) available with ${currentColor} color`}
          onClick={(e) => {
            if (query == null || disabled) e.preventDefault();
            setIsOpen(false);
            if (suggested) setSuggestedVariant(variant);
            if (!suggested) setSuggestedVariant('');
          }}
          onMouseOver={() => {
            onHover(currentColor);
            if (suggested) setIsOpen(true);
          }}
          onMouseOut={() => {
            onHoverOut();
            if (suggested) setIsOpen(false);
          }}
          className={cn(
            ANCHOR_BASE,
            {
              [ANCHOR_DISABLED]: query == null || disabled,
              [ANCHOR_INACTIVE]: query != null && !active && !suggested,
            },
            'relative inline p-2 xs:p-0',
          )}
          data-active={active && disabled && suggested}
          {...linkProps}
        >
          {isOpen && <Tooltip />}
          {children}
        </a>
      </Link>
    </div>
  );
};

export function Anchor(
  props: InputAttributes & {
    query: null | SelectionT;
    currentColor: string;
    active: boolean;
    onHover: (color: string) => void;
    onHoverOut: () => void;
    disabled?: boolean;
    suggested?: boolean | undefined;
    setSuggestedVariant: React.Dispatch<React.SetStateAction<string>>;
    variant: string;
  },
) {
  const { suggested } = props;

  return (
    <div className="flex items-center justify-center">
      {suggested ? (
        <div
          className="flex h-[45px] w-[45px] items-center justify-center xs:h-[33px] xs:w-[33px] xs:px-1"
          // eslint-disable-next-line react/forbid-dom-props
          style={{
            /* dashed border */
            backgroundImage: `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='100' ry='100' stroke='%23D8D9DDFF' stroke-width='2' stroke-dasharray='25%25%2c 20%25' stroke-dashoffset='60' stroke-linecap='square'/%3e%3c/svg%3e")`,
            borderRadius: '100px',
          }}
        >
          <Color {...props} />
        </div>
      ) : (
        <Color {...props} />
      )}{' '}
    </div>
  );
}

export function Group(props: {
  id: string;
  title: ReactNode | string;
  psku: string;
  children?: ReactElement[];
  selectedColor?: string;
  suggestedVariant: string;
}) {
  if (props.children == null || props.children?.length === 0) return null;

  return (
    <div className="mb-4">
      <h3 className="mb-1 text-[10px] font-bold uppercase text-gray-700 xs:mb-2 lg:font-normal">
        {props.title}: <b>{props.selectedColor}</b>
      </h3>
      {/* eslint-disable-next-line tailwindcss/no-custom-classname */}
      <div
        key={props.psku}
        className="mb-5 grid grid-cols-[repeat(auto-fill,50px)] justify-between gap-x-2 gap-y-1 xxs:gap-y-2 xs:grid-cols-[repeat(auto-fill,26px)] xs:gap-x-4 xs:gap-y-3 sm:gap-x-5"
        id={props.id}
      >
        {props.children}
      </div>
      {props.suggestedVariant === props.title?.toString().toLowerCase() && (
        <span className="mb-3 text-xs text-[#1C6C49]">
          Updated other options based on this selection
        </span>
      )}
    </div>
  );
}
